import React, { useState, useCallback, useMemo } from 'react';

export interface NumericFieldSetLabel {
  id?: string;
  name: string;
  value: number;
  color: string;
  icon: string;
  position: number;
  _destroy?: boolean;
}

export interface NumericFieldSetFormData {
  id?: string;
  name: string;
  units: 'none' | 'percentage';
  minValue: number;
  maxValue: number;
  labels: NumericFieldSetLabel[];
}

export interface NumericFieldSetFormLabels {
  title: string;
  minValue: string;
  maxValue: string;
  units: string;
  labels: string;
  labelName: string;
  value: string;
  addLabel: string;
  remove: string;
  save: string;
  cancel: string;
  noLabels: string;
  unitOptions: {
    none: string;
    percentage: string;
  };
}

export interface NumericFieldSetFormProps {
  initialData: NumericFieldSetFormData;
  onSubmit: (data: NumericFieldSetFormData) => void;
  onCancel: () => void;
  onLabelIconClick?: (labelIndex: number, currentIcon: string) => void;
  isSubmitting?: boolean;
  errors?: Record<string, string[]>;
  labels: NumericFieldSetFormLabels;
}

// Colors matching SCORE_COLORS from IconPicker (score -> color)
const SCORE_COLORS: Record<number, string> = {
  0: '#ff0000',
  12: '#ff4400',
  25: '#ff8800',
  37: '#ffBB00',
  50: '#fffb00',
  62: '#DDff00',
  75: '#BBff00',
  87: '#99ff00',
  100: '#00ff00',
};

const DEFAULT_ICONS = [
  'dislike-hands-and-gestures',
  'like-hands-and-gestures',
  'check',
  'angry',
  'emoji-cool',
];

/**
 * Get the color closest to a given value (0-100 scale).
 * Finds the nearest score in SCORE_COLORS and returns its color.
 */
export function getColorForValue(value: number): string {
  const scores = Object.keys(SCORE_COLORS).map(Number).sort((a, b) => a - b);
  if (scores.length === 0) return SCORE_COLORS[50] ?? '#fffb00';

  let closestScore = scores[0] as number;
  let minDiff = Math.abs(value - closestScore);

  for (const score of scores) {
    const diff = Math.abs(value - score);
    if (diff < minDiff) {
      minDiff = diff;
      closestScore = score;
    }
  }

  return SCORE_COLORS[closestScore] ?? SCORE_COLORS[50] ?? '#fffb00';
}

/**
 * Calculate the smart value for a new label based on existing label values.
 * Returns a unique value that doesn't exist in existingValues.
 *
 * Logic:
 * - Find the biggest gap between consecutive values
 * - Place the new value at the midpoint of that gap
 * - This ensures we always fill the largest empty space
 */
export function calculateSmartLabelValue(existingValues: number[]): number {
  if (existingValues.length < 2) {
    return 50; // Default midpoint
  }

  const sorted = [...existingValues].sort((a, b) => a - b);
  const usedSet = new Set(sorted);

  // Calculate all gaps and find the biggest one
  let biggestGap: { start: number; end: number; size: number } | null = null;

  for (let i = 0; i < sorted.length - 1; i++) {
    const start = sorted[i];
    const end = sorted[i + 1];
    if (start !== undefined && end !== undefined) {
      const size = end - start;
      if (!biggestGap || size > biggestGap.size) {
        biggestGap = { start, end, size };
      }
    }
  }

  // Safety check
  if (!biggestGap || biggestGap.size <= 1) {
    // No room to add a new value, find any unique value
    for (let v = 0; v <= 100; v++) {
      if (!usedSet.has(v)) return v;
    }
    return 50;
  }

  // Place at midpoint of the biggest gap
  const midpoint = Math.round((biggestGap.start + biggestGap.end) / 2);

  // Ensure it's unique (shouldn't happen with midpoint, but just in case)
  if (!usedSet.has(midpoint)) {
    return midpoint;
  }

  // If midpoint is taken, search within the gap for an available value
  for (let offset = 1; offset < biggestGap.size; offset++) {
    const lower = midpoint - offset;
    const upper = midpoint + offset;
    if (lower > biggestGap.start && !usedSet.has(lower)) return lower;
    if (upper < biggestGap.end && !usedSet.has(upper)) return upper;
  }

  return midpoint; // Fallback
}

function getContrastColor(hexColor: string): string {
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#ffffff';
}

export function NumericFieldSetForm({
  initialData,
  onSubmit,
  onCancel,
  onLabelIconClick,
  isSubmitting = false,
  errors = {},
  labels: l,
}: NumericFieldSetFormProps) {
  const [formData, setFormData] = useState<NumericFieldSetFormData>(initialData);

  // Get visible labels sorted by value
  const visibleLabels = useMemo(() => {
    return formData.labels
      .filter(label => !label._destroy)
      .sort((a, b) => a.value - b.value);
  }, [formData.labels]);

  // Determine if a label is first or last (cannot be deleted)
  const isFirstOrLastLabel = useCallback((label: NumericFieldSetLabel) => {
    if (visibleLabels.length <= 2) return true; // Always protect when 2 or fewer
    const sortedIndex = visibleLabels.indexOf(label);
    return sortedIndex === 0 || sortedIndex === visibleLabels.length - 1;
  }, [visibleLabels]);

  // Check if value is editable (first/last in percentage mode are fixed)
  const isValueEditable = useCallback((label: NumericFieldSetLabel) => {
    if (formData.units !== 'percentage') return true;
    const sortedIndex = visibleLabels.indexOf(label);
    return sortedIndex !== 0 && sortedIndex !== visibleLabels.length - 1;
  }, [formData.units, visibleLabels]);

  // Calculate effective min/max values
  const effectiveMinMax = useMemo(() => {
    if (formData.units === 'percentage') {
      return { minValue: 0, maxValue: 100 };
    }
    // For 'none' mode, max is the highest label value
    const maxLabelValue = visibleLabels.length > 0
      ? Math.max(...visibleLabels.map(l => l.value))
      : 100;
    return { minValue: 0, maxValue: maxLabelValue };
  }, [formData.units, visibleLabels]);

  const handleChange = useCallback((field: keyof NumericFieldSetFormData, value: string | number) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };

      // When switching to percentage mode, fix first and last label values
      if (field === 'units' && value === 'percentage') {
        const visible = prev.labels.filter(lab => !lab._destroy).sort((a, b) => a.value - b.value);
        if (visible.length >= 2) {
          const firstLabel = visible[0];
          const lastLabel = visible[visible.length - 1];
          newData.labels = prev.labels.map(lab => {
            if (lab === firstLabel) return { ...lab, value: 0 };
            if (lab === lastLabel) return { ...lab, value: 100 };
            return lab;
          });
        }
        newData.minValue = 0;
        newData.maxValue = 100;
      }

      return newData;
    });
  }, []);

  const handleLabelChange = useCallback((index: number, field: keyof NumericFieldSetLabel, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      labels: prev.labels.map((label, i) =>
        i === index ? { ...label, [field]: value } : label
      ),
    }));
  }, []);

  // Sort labels and validate unique values on blur
  const handleValueBlur = useCallback((index: number) => {
    setFormData(prev => {
      const label = prev.labels[index];
      if (!label || label._destroy) return prev;

      // Check for duplicate values
      const visible = prev.labels.filter(lab => !lab._destroy);
      const duplicateExists = visible.some(
        (lab, i) => lab !== label && lab.value === label.value
      );

      if (duplicateExists) {
        // Find a unique value by incrementing
        let newValue = label.value;
        const usedValues = new Set(visible.filter(lab => lab !== label).map(lab => lab.value));
        while (usedValues.has(newValue)) {
          newValue += 1;
        }
        return {
          ...prev,
          labels: prev.labels.map((lab, i) =>
            i === index ? { ...lab, value: newValue } : lab
          ),
        };
      }

      return prev;
    });
  }, []);

  const handleAddLabel = useCallback(() => {
    const visible = formData.labels.filter(lab => !lab._destroy).sort((a, b) => a.value - b.value);
    const existingValues = visible.map(lab => lab.value);

    // Calculate smart value based on existing labels (always finds midpoint of biggest gap)
    let newValue = calculateSmartLabelValue(existingValues);

    // Clamp to valid range
    newValue = Math.max(effectiveMinMax.minValue, Math.min(effectiveMinMax.maxValue, newValue));

    // Get color based on value (using SCORE_COLORS mapping)
    const valueBasedColor = getColorForValue(newValue);

    // Find icon from neighboring label:
    // - Use previous non-zero value label's icon
    // - Or next label's icon if previous has value 0
    let inheritedIcon = DEFAULT_ICONS[0] ?? 'circle';

    // Find where the new label would be inserted based on its value
    const insertIndex = visible.findIndex(lab => lab.value > newValue);

    if (insertIndex === -1) {
      // New label has highest value - use previous label (if exists and non-zero)
      const prevLabel = visible[visible.length - 1];
      if (prevLabel && prevLabel.value !== 0) {
        inheritedIcon = prevLabel.icon;
      } else if (visible.length > 1) {
        // Use second to last if last is zero
        const secondPrev = visible[visible.length - 2];
        if (secondPrev) {
          inheritedIcon = secondPrev.icon;
        }
      }
    } else if (insertIndex === 0) {
      // New label has lowest value - use next label
      const nextLabel = visible[0];
      if (nextLabel) {
        inheritedIcon = nextLabel.icon;
      }
    } else {
      // New label is in the middle - use previous non-zero or next
      const prevLabel = visible[insertIndex - 1];
      const nextLabel = visible[insertIndex];
      if (prevLabel && prevLabel.value !== 0) {
        inheritedIcon = prevLabel.icon;
      } else if (nextLabel) {
        inheritedIcon = nextLabel.icon;
      }
    }

    const nextPosition = visible.length + 1;

    const newLabel: NumericFieldSetLabel = {
      name: '',
      value: newValue,
      color: valueBasedColor,
      icon: inheritedIcon,
      position: nextPosition,
    };

    setFormData(prev => ({
      ...prev,
      labels: [...prev.labels, newLabel],
    }));
  }, [formData.labels, effectiveMinMax]);

  const handleRemoveLabel = useCallback((index: number) => {
    const label = formData.labels[index];
    if (!label) return;

    // Check if this is first or last - cannot delete
    if (isFirstOrLastLabel(label)) return;

    if (label.id) {
      // Mark existing label for destruction
      setFormData(prev => ({
        ...prev,
        labels: prev.labels.map((lab, i) =>
          i === index ? { ...lab, _destroy: true } : lab
        ),
      }));
    } else {
      // Remove new label from array
      setFormData(prev => ({
        ...prev,
        labels: prev.labels.filter((_, i) => i !== index),
      }));
    }
  }, [formData.labels, isFirstOrLastLabel]);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();

    // Prepare data with calculated min/max for 'none' mode
    const dataToSubmit = {
      ...formData,
      minValue: effectiveMinMax.minValue,
      maxValue: effectiveMinMax.maxValue,
      // Update positions based on sorted order
      labels: formData.labels.map(label => {
        if (label._destroy) return label;
        const sortedIndex = visibleLabels.indexOf(label);
        return { ...label, position: sortedIndex + 1 };
      }),
    };

    onSubmit(dataToSubmit);
  }, [formData, effectiveMinMax, visibleLabels, onSubmit]);

  // Update label icon and color when selected from icon picker
  const updateLabelIcon = useCallback((labelIndex: number, icon: string, color?: string) => {
    setFormData(prev => ({
      ...prev,
      labels: prev.labels.map((label, i) =>
        i === labelIndex ? { ...label, icon, ...(color ? { color } : {}) } : label
      ),
    }));
  }, []);

  // Expose updateLabelIcon method
  React.useEffect(() => {
    (window as unknown as Record<string, unknown>).__numericFieldSetFormUpdateLabelIcon = updateLabelIcon;
    return () => {
      delete (window as unknown as Record<string, unknown>).__numericFieldSetFormUpdateLabelIcon;
    };
  }, [updateLabelIcon]);

  return (
    <form onSubmit={handleSubmit}>
      {/* Error messages */}
      {Object.keys(errors).length > 0 && (
        <div className="alert alert-danger mb-3">
          <ul className="mb-0">
            {Object.entries(errors).flatMap(([field, messages]) =>
              messages.map((msg, i) => (
                <li key={`${field}-${i}`}>{msg}</li>
              ))
            )}
          </ul>
        </div>
      )}

      {/* Name */}
      <div className="form-group mb-3">
        <div className="form-floating">
          <input
            type="text"
            className="form-control"
            id="nfs-name"
            name="diaries_numeric_field_set[name]"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            required
            placeholder={l.title}
          />
          <label htmlFor="nfs-name">{l.title}</label>
        </div>
      </div>

      {/* Units */}
      <div className="mb-3">
        <div className="form-floating">
          <select
            className="form-select"
            id="nfs-units"
            name="diaries_numeric_field_set[units]"
            value={formData.units}
            onChange={(e) => handleChange('units', e.target.value as 'none' | 'percentage')}
          >
            <option value="none">{l.unitOptions.none}</option>
            <option value="percentage">{l.unitOptions.percentage}</option>
          </select>
          <label htmlFor="nfs-units">{l.units}</label>
        </div>
      </div>

      {/* Hidden min/max inputs for form submission */}
      <input type="hidden" name="diaries_numeric_field_set[min_value]" value={effectiveMinMax.minValue} />
      <input type="hidden" name="diaries_numeric_field_set[max_value]" value={effectiveMinMax.maxValue} />

      {/* Labels Section - styled like activities card */}
      <div className="card mb-3">
        <div className="card-header d-flex align-items-center">
          <h5 className="card-title mb-0 me-auto">
            <i className="bi bi-tags me-2" />
            {l.labels}
          </h5>
        </div>
        <div className="card-body">
          {visibleLabels.length === 0 ? (
            <p className="text-muted mb-3">{l.noLabels}</p>
          ) : (
            <div className="mb-3">
              {visibleLabels.map((label) => {
                const realIndex = formData.labels.indexOf(label);
                const textColor = getContrastColor(label.color);
                const canDelete = !isFirstOrLastLabel(label);
                const canEditValue = isValueEditable(label);
                const sortedIndex = visibleLabels.indexOf(label);

                return (
                  <div key={realIndex} className="d-flex align-items-center gap-2 mb-2">
                    {/* Icon button */}
                    <div
                      className="icon-wrapper"
                      style={{
                        backgroundColor: label.color,
                        cursor: 'pointer',
                        flexShrink: 0,
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      onClick={() => onLabelIconClick?.(realIndex, label.icon)}
                      data-controller="modal-trigger"
                      data-modal-trigger-target-value="iconModal"
                      data-label-index={realIndex}
                    >
                      <i
                        className={`icon icon-${label.icon || 'circle'}`}
                        style={{
                          display: 'inline-block',
                          width: 24,
                          height: 24,
                          backgroundSize: 'contain',
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'center',
                        }}
                      />
                    </div>

                    {/* Label name input */}
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={label.name}
                      onChange={(e) => handleLabelChange(realIndex, 'name', e.target.value)}
                      placeholder={l.labelName}
                      required
                      style={{
                        width: 150,
                        flexShrink: 0,
                        backgroundColor: 'rgba(255,255,255,0.9)',
                        borderColor: '#dee2e6',
                      }}
                    />

                    {/* Value input */}
                    <div className="input-group input-group-sm" style={{ width: 100 }}>
                      <input
                        type="number"
                        className="form-control"
                        value={label.value}
                        onChange={(e) => handleLabelChange(realIndex, 'value', parseInt(e.target.value, 10) || 0)}
                        onBlur={() => handleValueBlur(realIndex)}
                        min={effectiveMinMax.minValue}
                        max={effectiveMinMax.maxValue}
                        required
                        disabled={!canEditValue}
                      />
                      {formData.units === 'percentage' && (
                        <span className="input-group-text">%</span>
                      )}
                    </div>

                    {/* Remove button - only shown for middle labels */}
                    {canDelete ? (
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => handleRemoveLabel(realIndex)}
                        title={l.remove}
                      >
                        <i className="bi bi-trash" />
                      </button>
                    ) : (
                      <div style={{ width: 32 }} /> // Spacer to maintain alignment
                    )}

                    {/* Hidden fields for form submission */}
                    {label.id && (
                      <input
                        type="hidden"
                        name={`diaries_numeric_field_set[labels_attributes][${realIndex}][id]`}
                        value={label.id}
                      />
                    )}
                    <input
                      type="hidden"
                      name={`diaries_numeric_field_set[labels_attributes][${realIndex}][name]`}
                      value={label.name}
                    />
                    <input
                      type="hidden"
                      name={`diaries_numeric_field_set[labels_attributes][${realIndex}][value]`}
                      value={label.value}
                    />
                    <input
                      type="hidden"
                      name={`diaries_numeric_field_set[labels_attributes][${realIndex}][color]`}
                      value={label.color}
                    />
                    <input
                      type="hidden"
                      name={`diaries_numeric_field_set[labels_attributes][${realIndex}][icon]`}
                      value={label.icon}
                    />
                    <input
                      type="hidden"
                      name={`diaries_numeric_field_set[labels_attributes][${realIndex}][position]`}
                      value={sortedIndex + 1}
                    />
                  </div>
                );
              })}

              {/* Hidden fields for destroyed labels */}
              {formData.labels.filter(lab => lab._destroy && lab.id).map((label) => {
                const realIndex = formData.labels.indexOf(label);
                return (
                  <React.Fragment key={`destroy-${realIndex}`}>
                    <input
                      type="hidden"
                      name={`diaries_numeric_field_set[labels_attributes][${realIndex}][id]`}
                      value={label.id}
                    />
                    <input
                      type="hidden"
                      name={`diaries_numeric_field_set[labels_attributes][${realIndex}][_destroy]`}
                      value="1"
                    />
                  </React.Fragment>
                );
              })}
            </div>
          )}

          {/* Add Label Button */}
          <div className="text-end">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleAddLabel}
            >
              <i className="bi bi-plus-circle me-1" />
              {l.addLabel}
            </button>
          </div>
        </div>
      </div>

      {/* Form Buttons */}
      <div className="d-flex gap-2">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          {l.cancel}
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
              {l.save}
            </>
          ) : (
            l.save
          )}
        </button>
      </div>
    </form>
  );
}

export default NumericFieldSetForm;
